import { JSX, useState } from 'react';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import {
    CloudDownload,
    PlayArrow,
    PlayCircle,
    StopCircle,
} from '@mui/icons-material';
import {
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { Colors } from 'dg-web-shared/ui/vars.ts';
import { DateTime } from 'luxon';
import { FiltersBox } from '../shared-mui-components/filter/FiltersBox.tsx';
import {
    ValidFromDisplay,
    ValidFromValidToDisplay,
} from '../common/components/Transaction.tsx';
import {
    EmptyResults,
    ResultLimitAlert,
} from '../shared-mui-components/ListMessages.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { PaymentChannelLocalized } from '../common/components/PaymentChannelLocalized.tsx';
import { useOnstreetTransactionsFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import {
    ButtonWithDropdown,
    DropdownItem,
} from '../layout/components/ButtonWithDropdown.tsx';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { makeSearchQueryWithFilters } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { Outlet, useNavigate } from 'react-router-dom';
import {
    getTransactionAmount,
    getZoneName,
    LicensePlate,
    OnstreetRunningTransaction,
    OnstreetTerminatedTransaction,
    TomTransaction,
    Transaction,
    TransactionType,
} from './OperatorOnstreetTransactionShared.tsx';
import { Tom } from 'dg-web-shared/model/Tom.ts';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { useReadOnlyExportContext } from '../app/components/ReadOnlyExportContext.tsx';
import { ConfirmReadOnlyExport } from '../statistics/OperatorCheckinStatisticsRoute.tsx';

export interface OnstreetTransactionsSearchResult {
    eof: boolean;
    results: Transaction[];
}

const COLUMN_ZONE_MAX_WIDTH = 320;

export function OperatorOnstreetTransactionList() {
    const { operatorExternalId } = useOperatorContext();
    const navigate = useNavigate();
    const { language } = useLanguage();

    // check for TOMeco pay by space
    const [tomState] = useServerFetch<Tom[], object>(
        () => ({
            url: '/ui-api/operator-account/taxomex/tom',
        }),
        {},
    );
    const hasTomPayBySpace =
        Array.isArray(tomState.data) &&
        tomState.data.some(tom => tom.paymentType === 'pay_by_space');

    const {
        filterConfig,
        activeFilters,
        listState,
        refetchList,
        searchText,
        filterConfigState,
    } = useOnstreetTransactionsFilterConfiguration();

    const readOnlyTransactions = useReadOnlyExportContext();
    const [readOnlyExportModalOpen, setReadOnlyExportModalOpen] =
        useState(false);

    const [onstreetTransactionExportState, requestOnstreetTransactionExport] =
        useServerWrite<string, null>(() => ({
            url: `/ui-api/operator-account/onstreet-transactions/${language}/csv-export${makeSearchQueryWithFilters(
                searchText,
                activeFilters,
            )}`,
        }));

    useServerSuccessEffect(
        onstreetTransactionExportState,
        readOnlyTransactions.refetchReadOnlyExportData,
    );
    const hasRunningExport = readOnlyTransactions.runningExportId !== null;
    const notReady = listState.status !== RequestStatus.SUCCESS;

    const divider: DropdownItem = { key: 'divider', divider: true };

    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <>
                        <>Onstreet – </>
                        <OnstreetTransactionsTitle />
                    </>
                }
                icon={PlayArrow}
                headerButtons={
                    <Box
                        sx={{
                            marginLeft: 3,
                            marginRight: 3,
                        }}
                    >
                        <ButtonWithDropdown
                            variant="outlined"
                            label={
                                <Localized
                                    de="Exportieren"
                                    fr="Exporter"
                                    it="Esporta"
                                    en="Export"
                                />
                            }
                            startIcon={<CloudDownload />}
                            dropdownItems={[
                                {
                                    key: 'download-list',
                                    label: (
                                        <Localized
                                            de="Liste"
                                            fr="Liste"
                                            it="Lista"
                                            en="List"
                                        />
                                    ),
                                    tooltip: hasRunningExport ? (
                                        <Localized
                                            de="Ein export läuft bereits."
                                            fr="Une exportation est déjà en cours."
                                            it="È già in corso un'esportazione."
                                            en="An export is already running."
                                        />
                                    ) : notReady ? (
                                        <Localized
                                            de="Die Statistik ist noch nicht bereit."
                                            fr="Les statistiques ne sont pas encore prêtes."
                                            it="Le statistiche non sono ancora pronte."
                                            en="The statistics are not ready yet."
                                        />
                                    ) : (
                                        <Localized
                                            de="Die Transaktionen, die den Such-/Filterkriterien entsprechen, werde im CSV-Format exportiert."
                                            fr="Les transactions qui correspondent aux critères de recherche/filtrage sont exportées au format CSV."
                                            it="Le transazioni che corrispondono ai criteri di ricerca/filtro vengono esportate in formato CSV."
                                            en="The transactions that match the search/filter criteria are exported in CSV format."
                                        />
                                    ),
                                    disabled:
                                        onstreetTransactionExportState.status ===
                                            RequestStatus.PENDING ||
                                        hasRunningExport ||
                                        notReady,
                                    onClickCallback: () =>
                                        setReadOnlyExportModalOpen(true),
                                },
                                divider,
                                ...(hasTomPayBySpace
                                    ? [
                                          {
                                              key: 'tom-pay-by-space-transactions-export',
                                              label: (
                                                  <Localized
                                                      de="TOMeco-Transaktionen (mit Parkplatznummer)"
                                                      fr="Transactions TOMeco (avec numéro de place)"
                                                      it="Transazioni TOMeco (con numero di posto)"
                                                      en="TOMeco transactions (with parking space number)"
                                                  />
                                              ),
                                              tooltip: (
                                                  <Localized
                                                      de="Alle TOMeco-Transaktonen mit Parkplatznummer (pay by space) von den letzten 90 Tage werde im CSV-Format exportiert."
                                                      fr="Toutes les transactions TOMeco avec numéro de place (pay by space) des 90 derniers jours sont exportées au format CSV."
                                                      it="Tutte le transazioni TOMeco con il numero di posto (pay by space) degli ultimi 90 giorni vengono esportate in formato CSV."
                                                      en="All TOMeco transactions with parking space numbers (pay by space) from the last 90 days are exported in CSV format."
                                                  />
                                              ),
                                              onClickCallback: () => {
                                                  openNewTab(
                                                      urlWithOperatorExternalId(
                                                          `/ui-api/operator-account/onstreet-transactions/tom/export-pay-by-space?language=${language}`,
                                                          operatorExternalId,
                                                      ),
                                                  );
                                              },
                                              disabled: false,
                                          },
                                      ]
                                    : []),
                                {
                                    key: 'potential-gratis-abuse-export',
                                    label: (
                                        <Localized
                                            de="Verdächtige Kennzeichen (Gratiszeiten)"
                                            fr="Plaques suspectes (temps gratuits)"
                                            it="Targhe sospette (tempi gratuiti)"
                                            en="Suspicious plates (free times)"
                                        />
                                    ),
                                    tooltip: (
                                        <Localized
                                            de="Die Kennzeichen, die in den letzten 90 Tagen mehr als 30 kostenlose Transaktionen hatten, werden im Excel-Format exportiert."
                                            fr="Les plaques qui ont eu plus de 30 transactions gratuites au cours des 90 derniers jours sont exportées au format Excel."
                                            it="Le targhe che negli ultimi 90 giorni hanno avuto più di 30 transazioni gratuite, vengono esportate in formato Excel."
                                            en="Licence plates that have had more than 30 free transactions in the last 90 days are exported in Excel format."
                                        />
                                    ),
                                    onClickCallback: () => {
                                        openNewTab(
                                            urlWithOperatorExternalId(
                                                `/ui-api/operator-account/onstreet-transactions/potential-gratis-abuse-export?language=${language}`,
                                                operatorExternalId,
                                            ),
                                        );
                                    },
                                    disabled: false,
                                },
                            ]}
                        />
                    </Box>
                }
            />
            <FiltersBox
                searchText={searchText}
                activeFilters={activeFilters}
                filterConfig={filterConfig}
                requestState={listState}
                refetch={refetchList}
                pending={filterConfigState.status === RequestStatus.PENDING}
                tooltip={<OnstreetTransactionsSearchTooltip />}
            />
            <OperatorAsyncLoadedSection
                requestState={listState}
                render={response => (
                    <TransactionResponseTable
                        response={response}
                        onRowClick={transaction => {
                            navigate(
                                `contract/${transaction.contractExternalId}${makeSearchQueryWithFilters(
                                    searchText,
                                    activeFilters,
                                )}`,
                            );
                        }}
                    />
                )}
            />
            <Outlet />
            <ConfirmReadOnlyExport
                open={readOnlyExportModalOpen}
                onConfirm={() => {
                    requestOnstreetTransactionExport();
                    setReadOnlyExportModalOpen(false);
                }}
                onCancel={() => setReadOnlyExportModalOpen(false)}
            />
        </MainContent>
    );
}

export function OnstreetTransactionsTitle(): JSX.Element {
    const { operatorData } = useOperatorContext();

    if (operatorData.hasOnstreetTransactions && operatorData.hasTaxomexModule) {
        return (
            <Localized
                de="Parkvorgänge & TOMeco"
                fr="Procédures & TOMeco"
                it="Procedure & TOMeco"
                en="Procedures & TOMeco"
            />
        );
    } else if (operatorData.hasTaxomexModule) {
        return (
            <Localized
                de="TOMeco-Transaktionen"
                fr="Transactions TOMeco"
                it="Transazioni TOMeco"
                en="TOMeco transactions"
            />
        );
    } else {
        return (
            <Localized
                de="Parkvorgänge"
                fr="Procédures"
                it="Procedure"
                en="Procedures"
            />
        );
    }
}

function TransactionResponseTable({
    response,
    onRowClick,
}: {
    response: OnstreetTransactionsSearchResult;
    onRowClick: TransactionRowClickCallback;
}) {
    if (response.results.length === 0) {
        return <EmptyResults />;
    }

    return (
        <TableContainer>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Status"
                                fr="Statut"
                                it="Stato"
                                en="State"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Identifikation"
                                fr="Identification"
                                it="Identificazione"
                                en="Identification"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Zone"
                                fr="Zone"
                                it="Zona"
                                en="Zone"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Parkzeit"
                                fr="Heures de stationnement"
                                it="Orario di sosta"
                                en="Park time"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Dauer"
                                fr="Durée"
                                it="Durata"
                                en="Duration"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Kanal"
                                fr="Canal"
                                it="Canale"
                                en="Channel"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Bezahlt"
                                fr="Payé"
                                it="Pagato"
                                en="Paid"
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {response.results.map(transaction => (
                        <TransactionRow
                            key={transaction.contractExternalId}
                            transaction={transaction}
                            onRowClick={onRowClick}
                        />
                    ))}
                </TableBody>
            </Table>
            <ResultLimitAlert
                eof={response.eof}
                numberOfResults={response.results.length}
                hasExport={true}
            />
        </TableContainer>
    );
}

function TransactionRow({
    transaction,
    onRowClick,
}: {
    transaction: Transaction;
    onRowClick: TransactionRowClickCallback;
}) {
    switch (transaction.type) {
        case TransactionType.ONSTREET_RUNNING:
            return (
                <RunningTransactionRow
                    transaction={transaction}
                    onRowClick={onRowClick}
                />
            );
        case TransactionType.ONSTREET_TERMINATED:
            return (
                <TerminatedTransactionRow
                    transaction={transaction}
                    onRowClick={onRowClick}
                />
            );
        case TransactionType.TOM:
            return (
                <TomTransactionRow
                    transaction={transaction}
                    onRowClick={onRowClick}
                />
            );
    }
}

function RunningTransactionRow({
    transaction,
    onRowClick,
}: {
    transaction: OnstreetRunningTransaction;
    onRowClick: TransactionRowClickCallback;
}) {
    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => onRowClick(transaction)}
        >
            <TableCell>
                <TransactionIcon running={true} />
            </TableCell>
            <TableCell>
                <LicensePlate transaction={transaction} />
            </TableCell>
            <TableCell sx={{ maxWidth: COLUMN_ZONE_MAX_WIDTH }}>
                <ZoneName transaction={transaction} />
            </TableCell>
            <TableCell>
                <ValidFromDisplay validFrom={transaction.validFrom} />
            </TableCell>
            <TableCell>{EmDash}</TableCell>
            <TableCell>
                <PaymentChannelLocalized
                    paymentChannel={transaction.paymentChannel}
                />
            </TableCell>
            <TableCell>{EmDash}</TableCell>
        </TableRow>
    );
}

function TerminatedTransactionRow({
    transaction,
    onRowClick,
}: {
    transaction: OnstreetTerminatedTransaction;
    onRowClick: TransactionRowClickCallback;
}) {
    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => onRowClick(transaction)}
        >
            <TableCell>
                <TransactionIcon running={false} />
            </TableCell>
            <TableCell>
                <LicensePlate transaction={transaction} />
            </TableCell>
            <TableCell sx={{ maxWidth: COLUMN_ZONE_MAX_WIDTH }}>
                <ZoneName transaction={transaction} />
            </TableCell>
            <TableCell>
                <ValidFromValidToDisplay
                    validFrom={transaction.validFrom}
                    validTo={transaction.validTo}
                />
            </TableCell>
            <TableCell>
                <Duration transaction={transaction} />
            </TableCell>
            <TableCell>
                <PaymentChannelLocalized
                    paymentChannel={transaction.paymentChannel}
                />
            </TableCell>
            <TableCell>
                <TransactionAmount transaction={transaction} />
            </TableCell>
        </TableRow>
    );
}

function TomTransactionRow({
    transaction,
    onRowClick,
}: {
    transaction: TomTransaction;
    onRowClick: TransactionRowClickCallback;
}) {
    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => onRowClick(transaction)}
        >
            <TableCell>
                <TransactionIcon
                    running={
                        // TOMeco transactions that have been paid into the future
                        DateTime.fromISO(transaction.validTo) > DateTime.now()
                    }
                />
            </TableCell>
            <TableCell>
                <LicensePlate transaction={transaction} />
            </TableCell>
            <TableCell sx={{ maxWidth: COLUMN_ZONE_MAX_WIDTH }}>
                <ZoneName transaction={transaction} />
            </TableCell>
            <TableCell>
                <ValidFromValidToDisplay
                    validFrom={transaction.validFrom}
                    validTo={transaction.validTo}
                />
            </TableCell>
            <TableCell>
                <Duration transaction={transaction} />
            </TableCell>
            <TableCell>
                <PaymentChannelLocalized
                    paymentChannel={transaction.paymentChannel}
                />
            </TableCell>
            <TableCell>
                <TransactionAmount transaction={transaction} />
            </TableCell>
        </TableRow>
    );
}

function TransactionIcon({ running }: { running: boolean }) {
    if (running) {
        return (
            <TooltipIcon
                title={
                    <Localized
                        de="laufend"
                        fr="en cours"
                        it="in corso"
                        en="running"
                    />
                }
                icon={
                    <PlayCircle
                        sx={{
                            color: Colors.green,
                            fontSize: 'inherit',
                        }}
                    />
                }
            />
        );
    } else {
        return (
            <TooltipIcon
                title={
                    <Localized
                        de="abgeschlossen"
                        fr="terminée"
                        it="completata"
                        en="completed"
                    />
                }
                icon={
                    <StopCircle
                        sx={{
                            color: Colors.grey,
                            fontSize: 'inherit',
                        }}
                    />
                }
            />
        );
    }
}

function TooltipIcon({
    title,
    icon,
}: {
    title: JSX.Element;
    icon: JSX.Element;
}) {
    return (
        <Tooltip title={title} placement={'bottom-start'}>
            <Box
                sx={{
                    display: 'flex',
                }}
            >
                {/* Tooltip requires a single child element that can
                accept refs and event handlers; this Box wraps the icon
                to satisfy that and control layout. */}
                <Box
                    sx={{
                        fontSize: '28px',
                        display: 'flex',
                    }}
                >
                    {icon}
                </Box>
            </Box>
        </Tooltip>
    );
}

function TransactionAmount({
    transaction,
}: {
    transaction: OnstreetTerminatedTransaction | TomTransaction;
}) {
    return (
        <Box sx={{ whiteSpace: 'nowrap' }}>
            {getTransactionAmount(transaction)}
        </Box>
    );
}

type TransactionRowClickCallback = (transaction: Transaction) => void;

function ZoneName({ transaction }: { transaction: Transaction }) {
    return (
        <Box
            sx={{
                maxWidth: COLUMN_ZONE_MAX_WIDTH,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}
        >
            {getZoneName(transaction)}
        </Box>
    );
}

function Duration({
    transaction,
}: {
    transaction: OnstreetTerminatedTransaction | TomTransaction;
}) {
    return <Box sx={{ whiteSpace: 'nowrap' }}>{transaction.duration}</Box>;
}

function OnstreetTransactionsSearchTooltip() {
    return (
        <Box
            sx={{
                fontSize: 14,
                padding: '18px 18px 5px 18px',
                width: '300px',
            }}
        >
            <Localized
                de={
                    <>
                        Verfügbare Suchkriterien:
                        <ul>
                            <li>Kennzeichen</li>
                            <li>Transaktion-ID</li>
                        </ul>
                    </>
                }
                fr={
                    <>
                        Critères de recherche disponibles:
                        <ul>
                            <li>numéro de plaque</li>
                            <li>ID transaction</li>
                        </ul>
                    </>
                }
                it={
                    <>
                        Criteri di ricerca disponibili:
                        <ul>
                            <li>numero di targa</li>
                            <li>ID transazione</li>
                        </ul>
                    </>
                }
                en={
                    <>
                        Available search criteria:
                        <ul>
                            <li>number plate number</li>
                            <li>transaction ID</li>
                        </ul>
                    </>
                }
            />
        </Box>
    );
}
